import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import { ProductCard } from "./Shop";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";

const getDisplayImage = (source) =>
  source ? source.replace(/\.webp(?=($|\?))/, ".jpg") : "";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const {
    addToCart,
    isInWishlist,
    toggleWishlist,
  } = useContext(CartContext);

  const productImages = useMemo(() => {
    if (!product) return [];
    const images = Array.isArray(product.images)
      ? product.images.filter(Boolean).map(getDisplayImage)
      : [];
    return images.length > 0
      ? images
      : [getDisplayImage(product.image)].filter(Boolean);
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];

    const relevanceScore = (item) =>
      Number(item.category === product.category) * 2 +
      Number(item.gender === product.gender);

    return productData
      .filter((item) => item.id !== product.id)
      .sort((first, second) =>
        relevanceScore(second) - relevanceScore(first)
      )
      .slice(0, 4);
  }, [product]);

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [sizeError, setSizeError] = useState("");
  const [addedMessage, setAddedMessage] = useState("");
  const [imageStatus, setImageStatus] = useState(
    productImages.length > 0 ? "loading" : "error"
  );
  const [imageRetry, setImageRetry] = useState(0);
  const [openPanels, setOpenPanels] = useState({
    details: true,
    materials: false,
    delivery: false,
  });
  const sizeGroupRef = useRef(null);

  useEffect(() => {
    setSelectedImage(0);
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedSize("");
    setQuantity(1);
    setSizeError("");
    setAddedMessage("");
    setImageRetry(0);
  }, [product]);

  useEffect(() => {
    setImageStatus(productImages[selectedImage] ? "loading" : "error");
  }, [productImages, selectedImage]);

  useEffect(() => {
    const previousTitle = document.title;
    let descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute("content") ?? null;
    let createdDescriptionTag = false;

    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
      createdDescriptionTag = true;
    }

    if (product) {
      document.title = `${product.name} | ${siteConfig.brandName}`;
      descriptionTag.setAttribute(
        "content",
        `${product.description || siteConfig.description} ${
          product.attributes?.material || ""
        }`
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 160)
      );
    } else {
      document.title = `Product not found | ${siteConfig.brandName}`;
      descriptionTag.setAttribute("content", siteConfig.description);
    }

    return () => {
      document.title = previousTitle;
      if (createdDescriptionTag) {
        descriptionTag.remove();
      } else if (previousDescription === null) {
        descriptionTag.removeAttribute("content");
      } else {
        descriptionTag.setAttribute("content", previousDescription);
      }
    };
  }, [product]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found" id="main-content">
        <section
          className="product-detail__not-found"
          aria-labelledby="product-not-found-title"
        >
          <p>404 · Product unavailable</p>
          <h1 id="product-not-found-title">We could not find this piece</h1>
          <span>
            It may have moved or left the collection. Explore the current edit
            instead.
          </span>
          <Link to="/shop" onClick={scrollToTop}>
            Return to the collection
          </Link>
        </section>
      </main>
    );
  }

  const activeImage = productImages[selectedImage] || productImages[0];
  const isWishlisted = isInWishlist(product.id);
  const isInStock = product.inStock !== false;
  const hasSizes = Array.isArray(product.size) && product.size.length > 0;
  const hasColours = Array.isArray(product.colors) && product.colors.length > 0;

  const selectImage = (index) => {
    setSelectedImage(index);
    setImageRetry(0);
    setImageStatus("loading");
  };

  const selectSize = (size) => {
    setSelectedSize(size);
    setSizeError("");
    setAddedMessage("");
  };

  const handleAddToCart = () => {
    if (!isInStock) return;

    if (hasSizes && !selectedSize) {
      setSizeError("Choose a size before adding this item to your bag.");
      setAddedMessage("");
      window.requestAnimationFrame(() => sizeGroupRef.current?.focus());
      return;
    }

    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAddedMessage(
      `${quantity} ${quantity === 1 ? "item" : "items"} added to your bag.`
    );
    setQuantity(1);
  };

  const togglePanel = (panel) => {
    setOpenPanels((current) => ({ ...current, [panel]: !current[panel] }));
  };

  return (
    <main className="product-detail" id="main-content">
      <div className="product-detail__shell">
        <nav className="product-breadcrumb" aria-label="Breadcrumb">
          <ol>
            <li>
              <Link to="/" onClick={scrollToTop}>
                Home
              </Link>
            </li>
            <li>
              <Link to="/shop" onClick={scrollToTop}>
                Shop
              </Link>
            </li>
            <li>
              <span aria-current="page">{product.name}</span>
            </li>
          </ol>
        </nav>

        <div className="product-detail__main">
          <section className="product-gallery" aria-label="Product images">
            {productImages.length > 1 && (
              <div className="product-gallery__thumbs" aria-label="Choose image">
                {productImages.map((image, index) => (
                  <button
                    type="button"
                    key={`${image}-${index}`}
                    className={index === selectedImage ? "is-active" : ""}
                    onClick={() => selectImage(index)}
                    aria-label={`Show ${product.name} image ${index + 1}`}
                    aria-pressed={index === selectedImage}
                  >
                    <img
                      src={image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            )}

            <div
              className={`product-gallery__stage${
                productImages.length <= 1 ? " product-gallery__stage--single" : ""
              }`}
            >
              {activeImage && (
                <img
                  key={`${activeImage}-${imageRetry}`}
                  className={imageStatus === "loaded" ? "is-loaded" : ""}
                  src={activeImage}
                  alt={`${product.name}${
                    selectedColor ? ` in ${selectedColor}` : ""
                  }`}
                  onLoad={() => setImageStatus("loaded")}
                  onError={() => setImageStatus("error")}
                  decoding="async"
                />
              )}

              {imageStatus === "loading" && (
                <div className="product-gallery__loading" role="status">
                  <span aria-hidden="true" />
                  <p>Loading image…</p>
                </div>
              )}

              {imageStatus === "error" && (
                <div className="product-gallery__error" role="alert">
                  <span aria-hidden="true">Image unavailable</span>
                  <p>We could not load this product image.</p>
                  {activeImage && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageStatus("loading");
                        setImageRetry((current) => current + 1);
                      }}
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>

          <aside className="product-purchase" aria-labelledby="product-title">
            <div className="product-purchase__inner">
              <div className="product-purchase__badges">
                {product.isNew && <span>New</span>}
                {product.compareAt && <span>Sale</span>}
                {product.featured && <span>Featured</span>}
              </div>

              <p className="product-purchase__category">
                {product.gender} · {product.category}
              </p>
              <h1 id="product-title">{product.name}</h1>

              <div className="product-purchase__pricing">
                <strong>{formatPrice(product.price)}</strong>
                {product.compareAt && (
                  <del>{formatPrice(product.compareAt)}</del>
                )}
              </div>
              <p className="product-purchase__tax">
                VAT included. Delivery calculated at checkout.
              </p>
              <p className="product-purchase__sku">SKU {product.sku}</p>

              <div
                className={`product-purchase__stock${
                  isInStock ? " is-available" : " is-unavailable"
                }`}
              >
                <span aria-hidden="true" />
                {isInStock
                  ? `In stock · Dispatch in ${siteConfig.shipping.dispatchWindow}`
                  : "Currently unavailable"}
              </div>

              {hasColours && (
                <fieldset className="product-option product-option--colours">
                  <legend>
                    Colour <strong>{selectedColor}</strong>
                  </legend>
                  <div className="product-colours">
                    {product.colors.map((colour, index) => (
                      <button
                        type="button"
                        key={colour}
                        className={colour === selectedColor ? "is-selected" : ""}
                        onClick={() => {
                          setSelectedColor(colour);
                          setAddedMessage("");
                        }}
                        aria-label={`Select colour ${colour}`}
                        aria-pressed={colour === selectedColor}
                        title={colour}
                      >
                        <span
                          style={{
                            backgroundColor:
                              product.colorHex?.[index] || "#b8b6ae",
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              {hasSizes && (
                <fieldset
                  className={`product-option product-option--sizes${
                    sizeError ? " has-error" : ""
                  }`}
                  ref={sizeGroupRef}
                  tabIndex="-1"
                  aria-describedby={sizeError ? "product-size-error" : undefined}
                >
                  <legend>
                    Size <strong>{selectedSize || "Select a size"}</strong>
                  </legend>
                  <div className="product-sizes" id="product-size-options">
                    {product.size.map((size) => (
                      <button
                        type="button"
                        key={size}
                        className={size === selectedSize ? "is-selected" : ""}
                        onClick={() => selectSize(size)}
                        aria-pressed={size === selectedSize}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  {sizeError && (
                    <p id="product-size-error" role="alert">
                      {sizeError}
                    </p>
                  )}
                </fieldset>
              )}

              <div className="product-quantity">
                <span>Quantity</span>
                <div role="group" aria-label="Quantity">
                  <button
                    type="button"
                    onClick={() => setQuantity((current) => current - 1)}
                    disabled={quantity <= 1}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <output aria-live="polite">{quantity}</output>
                  <button
                    type="button"
                    onClick={() => setQuantity((current) => current + 1)}
                    disabled={quantity >= 10}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="product-purchase__add"
                onClick={handleAddToCart}
                disabled={!isInStock}
              >
                {isInStock ? "Add to bag" : "Unavailable"}
              </button>
              <button
                type="button"
                className={`product-purchase__wishlist${
                  isWishlisted ? " is-active" : ""
                }`}
                onClick={() => toggleWishlist(product.id)}
                aria-pressed={isWishlisted}
              >
                <span aria-hidden="true">{isWishlisted ? "♥" : "♡"}</span>
                {isWishlisted ? "Saved to wishlist" : "Add to wishlist"}
              </button>

              <div className="product-purchase__message" aria-live="polite">
                {addedMessage && (
                  <p>
                    {addedMessage}{" "}
                    <Link to="/cartpage" onClick={scrollToTop}>
                      View bag
                    </Link>
                  </p>
                )}
              </div>

              <p className="product-purchase__shipping-note">
                Free standard delivery from{" "}
                {formatPrice(siteConfig.shipping.freeThreshold)} ·{" "}
                {siteConfig.shipping.returnsWindow}-day returns
              </p>

              <div className="product-accordions">
                <section>
                  <h2>
                    <button
                      type="button"
                      id="product-details-trigger"
                      aria-expanded={openPanels.details}
                      aria-controls="product-details-panel"
                      onClick={() => togglePanel("details")}
                    >
                      Product details
                      <span aria-hidden="true">
                        {openPanels.details ? "−" : "+"}
                      </span>
                    </button>
                  </h2>
                  <div
                    id="product-details-panel"
                    role="region"
                    aria-labelledby="product-details-trigger"
                    hidden={!openPanels.details}
                  >
                    <p>{product.description}</p>
                    <dl>
                      <div>
                        <dt>Fit</dt>
                        <dd>{product.attributes?.fit}</dd>
                      </div>
                      <div>
                        <dt>Made in</dt>
                        <dd>{product.attributes?.origin}</dd>
                      </div>
                    </dl>
                  </div>
                </section>

                <section>
                  <h2>
                    <button
                      type="button"
                      id="product-materials-trigger"
                      aria-expanded={openPanels.materials}
                      aria-controls="product-materials-panel"
                      onClick={() => togglePanel("materials")}
                    >
                      Materials & care
                      <span aria-hidden="true">
                        {openPanels.materials ? "−" : "+"}
                      </span>
                    </button>
                  </h2>
                  <div
                    id="product-materials-panel"
                    role="region"
                    aria-labelledby="product-materials-trigger"
                    hidden={!openPanels.materials}
                  >
                    <dl>
                      <div>
                        <dt>Composition</dt>
                        <dd>{product.attributes?.material}</dd>
                      </div>
                      <div>
                        <dt>Care</dt>
                        <dd>{product.attributes?.care}</dd>
                      </div>
                    </dl>
                  </div>
                </section>

                <section>
                  <h2>
                    <button
                      type="button"
                      id="product-delivery-trigger"
                      aria-expanded={openPanels.delivery}
                      aria-controls="product-delivery-panel"
                      onClick={() => togglePanel("delivery")}
                    >
                      Delivery & returns
                      <span aria-hidden="true">
                        {openPanels.delivery ? "−" : "+"}
                      </span>
                    </button>
                  </h2>
                  <div
                    id="product-delivery-panel"
                    role="region"
                    aria-labelledby="product-delivery-trigger"
                    hidden={!openPanels.delivery}
                  >
                    <p>
                      Standard delivery is {formatPrice(
                        siteConfig.shipping.standardRate
                      )}, or free when your order reaches{" "}
                      {formatPrice(siteConfig.shipping.freeThreshold)}. Returns
                      are accepted within {siteConfig.shipping.returnsWindow}{" "}
                      days.
                    </p>
                    <Link to="/shipping" onClick={scrollToTop}>
                      Read delivery information
                    </Link>
                  </div>
                </section>
              </div>
            </div>
          </aside>
        </div>

        {relatedProducts.length > 0 && (
          <section
            className="product-related"
            aria-labelledby="related-products-title"
          >
            <div className="product-related__heading">
              <div>
                <p>Continue exploring</p>
                <h2 id="related-products-title">You may also like</h2>
              </div>
              <Link to="/shop" onClick={scrollToTop}>
                View all
              </Link>
            </div>
            <div className="product-related__grid">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
